import React, { createContext, useContext, useState, useEffect } from 'react';
import { authFetch } from '../utils/authFetch';
import { BASE_URL } from '../utils/config';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true);

  const login = (userData) => {
    console.log("AuthContext: login function called with userData:", userData);
    setIsAuthenticated(true);
    setCurrentUser(userData);
  };

  const logout = () => {
  console.log("AuthContext: logout function called. Clearing all localStorage.");
  localStorage.clear(); // ✅ Clears everything
  setIsAuthenticated(false);
  setCurrentUser(null);
};

  const fetchAndUpdateUser = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn("AuthContext: No token found for fetchAndUpdateUser. Logging out.");
      logout();
      return null;
    }

    const response = await authFetch(`${BASE_URL}/users/user`);
    const data = await response.json();
    
    console.log("🔍 AuthContext: Fetched user data from backend:", data);
    console.log("🔍 AuthContext: UserSubscription:", data.UserSubscription);

   if (response.ok && data) {
      // ✅ Use the data object directly, since it's at the top level
      const updatedUser = {
        ...data,
        isSubscribed: data.isSubscribed
      };

      setCurrentUser(updatedUser);
      setIsAuthenticated(true);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      console.log("🔍 AuthContext: Updated currentUser state:", updatedUser);
      return updatedUser;
    } else {
      console.error("AuthContext: Failed to fetch user data (response not ok or data missing):", data.message || data);
      logout();
      return null;
    }
  } catch (error) {
    console.error("AuthContext: Error during user data refresh (fetchAndUpdateUser):", error);
    logout();
    return null;
  }
};

  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const userString = localStorage.getItem('user');
        const token = localStorage.getItem('token');

        if (userString && userString !== "undefined" && userString !== "null" && token) {
          let user = null;
          try {
            user = JSON.parse(userString);
            console.log("AuthContext: Successfully parsed user from localStorage:", user);
          } catch (parseError) {
            console.error("AuthContext: Error parsing userString from localStorage:", parseError);
            user = null;
          }

          if (user && user.email) {
            console.log("AuthContext: Valid user found (by email) in localStorage. Setting auth state.");

            setIsAuthenticated(true);
            setCurrentUser(user);
          } else {
            // Don't clear localStorage if we're just viewing public pages
            // Only clear if user data is invalid but token exists (corrupted state)
            if (token) {
              console.warn("AuthContext: Token exists but user data is invalid. Clearing auth state without clearing localStorage.");
              setIsAuthenticated(false);
              setCurrentUser(null);
            } else {
              // No token - just set auth state to false, don't clear localStorage
              setIsAuthenticated(false);
              setCurrentUser(null);
            }
          }
        } else {
          // No user or token - just set auth state to false, don't clear localStorage
          // This is normal for public pages or new tabs
          setIsAuthenticated(false);
          setCurrentUser(null);
        }
      } catch (error) {
        console.error("AuthContext: Error during auth check:", error);
        // Don't clear localStorage on error, just set auth state
        setIsAuthenticated(false);
        setCurrentUser(null);
      } finally {
        setLoadingAuth(false);
      }
    };
    checkAuthStatus();
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, currentUser, loadingAuth, login, logout, fetchAndUpdateUser }}>
      {children}
    </AuthContext.Provider>
  );
};
